import React, { useState, SyntheticEvent } from 'react';
import Mips from '../charts/Mips';
import Tdp from '../charts/Tdp';
import TdpToMips from '../charts/TdpToMips';
import Footer from '../components/Footer';
import AppleIntel from '../charts/AppleIntel';

function BecomingEfficient() {
    return (
        <div className="container">
            <div className="row">
                <h2>Becoming Efficient</h2>
                <p>
                    Apple <a rel="noopener noreferrer" target="_blank" href="https://www.apple.com/newsroom/2020/06/apple-announces-mac-transition-to-apple-silicon/">recently recent announced the transition</a> of their Macintosh (iMac, MacBook, Pro, etc) line of computers to the <a rel="noopener noreferrer" target="_blank" href="https://www.arm.com/why-arm/architecture?utm_source=google&utm_medium=cpc&utm_campaign=2020_enterprise-marketing_mk30_na_architecture_bol_awa&utm_content=product-page&utm_term=%2Barm%20%2Bcpu%20%2Barchitecture&gclid=EAIaIQobChMI6s-wktPe6gIVDT2tBh3Z3QO6EAAYASAAEgKsCvD_BwE">Arm CPU architecture</a>. 
                    Arm is a microarchitecture known for being highly energy efficient, but traditionally not known for being as performant as the x86 architecture.
                    With Apple's announcement, and business decision in equipping their computers with Arm processors, they have firmly established their opinion in that their Apple ARM SoCs have become powerful enough to replace the use of x86 CPUs within their Macintosh computer line.
                </p>
                <div className="main-chart-container">
                    <hr/>
                    <AppleIntel />
                    <hr/>
                </div>
                <p>
                    For quite some time, Apple had only equipped their mobile devices such as the iPhone, and iPad with the Apple A-series SoC. With the introduction of the <a target="_blank" rel="nooopener noreferrer" href="https://en.wikipedia.org/wiki/IPad_Air_2">iPad Air 2</a> in late 2014, more use cases that have been in the past, performed on traditional Apple desktop machines are now on the Apple mobile devices. 
                    By 2018, it became quite clear to Apple that the multi-core performance of its low-wattage Arm SoCs had surpassed the mainstream Intel Core i7 mobile line of CPUs. 
                </p>
                <p>
                    In 2014, AMD also started an initiative called <a rel="noopener noreferrer" target="_blank" href="https://www.amd.com/en/technologies/25x20">25x20</a>. AMD had claimed in 2014, that with the 25x20 initiative, future mobile CPUs would be up to 25 times more energy efficient in 2020 compared to then present day. 
                    Reaching the goal earlier this year, with the Renoir line of CPUs, AMD has announced that the efforts in fulfilling the 25x20 initiative lead to development of mobile CPUs being <strong>31.7x</strong> more energy efficient in specific scenarios!
                </p>
                <p>
                    We are now capable of possessing massive amounts of computing power which can fit within the pockets of our clothes in the form of a smartphone. Even more 
                    amazing is that these phones can do more than what the highest performing computers could do over a decade ago. All this is achieved with only relying on battery technology for power, and 
                    absolutely no fans for cooling. This is a testament as to how far the world has come in the realm of efficiency in performance to energy consumption for computing.
                </p>
            </div>
        </div>
    );
}

function ChartIntroduction(props: any) {
    const {changeSelection} = props;
    return (
        <div className="container">
            <div className="row">
                <h2>The March Towards Energy Efficiency</h2>
                <p>
                    How far have we come over the years since the first mainstream consumer desktop, the <a rel="noopener noreferrer" target="_blank" href="https://en.wikipedia.org/wiki/IBM_Personal_Computer">IBM PC 5150</a>? We often hear of computers becoming 
                    exponentially powerful over time, but has the world also made progress in power efficiency? While we have made much progress in increasing the amount of millions of instructions per second in processing, has the same occurred for power consumption? 
                    The <strong>Thermal Design Power (TDP)</strong> of a CPU 
                    is the maximum amount of heat generated by the processor under typical workload scenarios.
                </p>
                <p>
                    Let's journey through time, and appreciate the performance growth from the years <strong>1971</strong> to <strong>2019</strong>: <a id="mips-link" onClick={changeSelection}>MIPS over Time</a>, and <a id="tdp-link" onClick={changeSelection}>TDP over Time</a>. 
                </p>
    
            </div>
        </div>
    );
}

function ChartEfficiencyExplanation() {
    return (
        <div className="container">
            <div className="row">
                <p>
                    Fortunately, as CPUs have become exponentially powerful through processing more instructions per second, the TDP has also increased, but not in an exponential manner. 
                </p>
            </div>
        </div>
    );
}

function Conclusion() {
    return (
        <div className="container">
            <div className="row">
                <h2>Conclusion</h2>
                <p>
                    There is significant progress in energy efficiency over the years. Measuring efficiency through calculation of TDP in watts per MIPS gives a better idea on the progress
                    in creating energy efficient CPUs. 
                </p>
                <p>
                    Although raw TDP has not decreased in the latest CPUs, the performance has increased dramatically. Thus, the ratio between 
                    watt per MIPS has decreased. We now need very little power to process the same amount of instructions year after year.
                </p>
            </div>
        </div>
    );
}

function Introduction() {
    return (
        <div className="container">
            <div className="row">
                <h2>There was a boy...</h2>
                <p>
                    In 2001, I was a desperate fifth grader in need of some more computing power to be able to hang with the cool kids in school. 
                    Everyone was playing one of the latest PC video games like <a rel="noopener noreferrer" target="_blank" href="https://www.mobygames.com/game/diablo-ii">Diablo 2</a>, and <a rel="noopener noreferrer" target="_blank" href="https://www.mobygames.com/game/half-life-counter-strike">Counter-Strike</a>.
                    I wasn't, and I wanted to be cool! I wanted to be part of the crowd!
                </p>
                <p>
                    Instead, my primary family desktop computer was an obsolete <a rel="noopener noreferrer" target="_blank" href="https://en.wikipedia.org/wiki/Intel_DX4">Intel DX4-100</a>. While it was able to run some 3D MS-DOS games, and the occasional
                    session of <a rel="noopener noreferrer" target="_blank" href="https://problemkaputt.de/gmb.htm">Gameboy emulation</a>, I wanted to play the latest and greatest game just like everyone else!
                </p>
                <p>
                    I begged my parents for a new computer that year for my birthday. Being the awesome parents they were, they had got me a <a rel="noopener noreferrer" target="_blank" href="https://www.cnet.com/products/compaq-presario-5122la-piii-1-ghz-128-mb-40-gb/">Compaq Presario Pentium 3</a>-based
                    system from the local Costco Wholesale that Spring of 2001. Finally! I could play the same games all my friends were playing at home!
                    I could finally play all the silly <a rel="noopener noreferrer" target="_blank" href="http://neopets.com">Flash games (Neopets)</a> on the internet! I could design my own web pages better on <a rel="noopener noreferrer" target="_blank" href="https://en.wikipedia.org/wiki/Yahoo!_GeoCities">Yahoo GeoCities</a>! 
                    I was happy. Well, until I had another phase.
                </p>
                <p>
                    A couple of years later, now in middle school, I had decided to dabble more in video game console emulation. One of the systems I had really wanted to emulate was the <a rel="noopener noreferrer" target="_blank" href="https://en.wikipedia.org/wiki/Nintendo_64">Nintendo 64</a>. 
                    I enjoyed the library of games on the N64, and really wanted to play it on my computer without needing to go to a friends house to enjoy these games. So, I downloaded
                    a version of <a rel="noopener noreferrer" target="_blank" href="https://en.wikipedia.org/wiki/UltraHLE">UltraHLE</a>, a popular Nintendo 64 emulator at the time, and attempted to play Super Mario 64.
                </p>
                <p>
                    Instead of getting a nice 3D experience, I got 5 fps gaming glory! My Compaq Presario only had an integrated graphics accelerator, and I had hoped software rendering within the 
                    application would at least help me get by. Unfortunately, I shut off the computer that Saturday afternoon rather disappointed.
                </p>
                <p>
                    A couple of weeks later, I somehow acquired a copy of the <a rel="noopener noreferrer" target="_blank" href="https://www.mobygames.com/game/elder-scrolls-iii-morrowind">Elder Scrolls III: Morrowind</a>, and hurridly installed it onto my computer. Ignoring the fact that my computer didn't even meet 
                    minimum system requirements for graphics to run the game, it came to no surprise that Morrowind ran just as bad as UltraHLE on that very same system. Yet, another blazing 5 fps gaming experience.
                </p>
                <p>
                    At this point, I had convinced myself that I was going to ask my parents for a computer upgrade. At the end of my 7th grade school year, I made sure to get the best grades possible, and spent a lot of 
                    time <a rel="noopener noreferrer" target="_blank" href="http://209.68.14.80/byop/index.htm">learning how to build a computer</a> in order to convince my parents I could do it.
                </p>
                <p>
                    One weekend, my mom made my dad drive me to the local <a rel="noopener noreferrer" target="_blank" href="https://en.wikipedia.org/wiki/Fry's_Electronics">Fry's Electronics</a>. After having spent the whole afternoon picking out components, my dad and I ultimately decided on a high-end <a rel="noopener noreferrer" target="_blank" href="https://en.wikipedia.org/wiki/Pentium_4#Northwood">Pentium 4</a>-based system with 
                    a powerful (at the time) <a rel="noopener noreferrer" target="_blank" href="https://www.techpowerup.com/gpu-specs/radeon-9700-pro.c50">ATI Radeon 9700 Pro</a> GPU.
                </p>
                <p>
                    Everything about the computer was awesome, but it wasn't until the summer of that year in 2003 when I noticed that my new computer ran quite hot. It was way hotter, and even worse, louder than my previous Pentium 3 system. 
                    Even with 4 additional case fans, 1 large CPU fan, 1 GPU fan, for a total of 6 fans could not cool the system as efficiently as the little CPU fan as the <em>only</em> fan in my older Pentium 3 system.
                </p>
                <p>
                    Had computer performance increased dramatically for the trade-off in energy effiency?
                </p>
                <p>
                    Until 2009, I had upgraded to several other systems, and all had end up running just as loud, and hot as my initial Pentium 4 system. It wasn't until I was a college student in 2009 that I had upgraded to a <a rel="noopener noreferrer" target="_blank" href="https://www.tomshardware.com/reviews/intel-cpu-power-consumption,1750.html">Core 2</a> based system when I was finally reunited with the relatively similar thermals as my Pentium 3 system, but with several magnitudes in performance improvement!
                </p>
                <p>
                    We may have to thank the mobile computing revolution for all the performance improvements being made to energy efficency in microprocessors over the last decade.
                </p>
            </div>
        </div>
    );
}

export default function Home() {
    const [activeChart, setActiveChart] = useState(0);

    function change(e: SyntheticEvent) {
        const select = document.getElementById("chart-select") as HTMLSelectElement;
        
        const target = (e.target as HTMLElement).id;
        if (target === "chart-select")
            setActiveChart(select.options.selectedIndex);
        else if(target === "mips-link") {
            select.options.selectedIndex = 0;
            setActiveChart(0);
        } else {
            select.options.selectedIndex = 1;

            setActiveChart(1);
        }
    }

    return (
        <div>
            <div className="jumbotron jumbotron-fluid">
                <div className="container">
                    <h3>
                        Computer Power and Efficiency Through the Years
                    </h3>
                    <p className="lead">
                        How far has the world progressed in computing power and efficiency since 1971 to 2019?
                    </p>
                </div>
            </div>
            <div className="main-content justify-content-center">
                <Introduction />
                <BecomingEfficient />
                <ChartIntroduction changeSelection={change} />
                <hr />
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-6">
                            <div className="form-group">
                                <label>Select a Chart</label>
                                <select onChange={change} className="form-control" id="chart-select">
                                    <option value="MIPS" selected>MIPS over Time</option>
                                    <option value="TDP">TDP over Time</option>
                                </select>
                            </div>

                        </div>

                    </div>
                </div>
                <div className="main-chart-container">
                    {
                        activeChart === 0 ? <Mips /> : <Tdp />
                    }
                </div>
                <hr />
                <ChartEfficiencyExplanation />
                <Conclusion />
                <TdpToMips />
            </div>
            <Footer />
        </div>
    );
}
